import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Anchor,
    Badge,
    Code,
    Group,
    Stack,
    Table,
    Text,
} from '@mantine/core';

import config from '../config';
import {Certificate} from '../types';

// FNP-1193 — WHAT THE PROVIDER WANTS PUBLISHED, AND WHETHER FLEXI PUBLISHED IT.
//
// ⚠️ THIS PANEL IS THE ANSWER TO "WHY IS THIS CERTIFICATE NOT ISSUED YET".
//
// Flexi writes the validation record itself through the owning zone's adapter,
// so a tenant should never copy it by hand — that hand-copying is precisely the
// step this module exists to remove. But when validation is STUCK, the record
// is the first thing anyone wants to compare against what is actually in the
// zone, and making them go and find it elsewhere is the friction the ticket is
// about.
//
// ⚠️ THE TWO NON-PROGRESSING STATES GET DIFFERENT PANELS, because they have
// different fixes. This component branches on `validationRecordWrittenAt`
// being null rather than on the status string, because that field IS the
// distinction — the status is computed from it.
export const ValidationRecordsPanel = (
    {
        certificate,
    }: {
        certificate: Certificate,
    },
) => {

    // ⚠️ NULL MEANS FLEXI NEVER RECORDED WRITING THE RECORD. Not "we are not
    // sure", not "it is in progress" — the write did not happen, and nothing is
    // waiting on DNS propagation.
    const neverWritten = !certificate.validationRecordWrittenAt;

    const records = certificate.validationRecords ?? [];

    // Issued certificates have no outstanding validation records, and saying
    // nothing is the right amount to say about a certificate that works.
    if (certificate.status === 'ISSUED' && records.length === 0) {
        return null;
    }

    return (
        <Stack gap={'sm'}>

            {/* ⚠️ THE `VALIDATION_NOT_WRITTEN` EXPLANATION. Deliberately does
                NOT say "wait" — the whole point of separating this from STUCK
                is that waiting is the wrong advice here. */}
            {neverWritten && certificate.status !== 'ISSUED' && (
                <Alert
                    color={'orange'}
                    variant={'light'}
                    title={I18n.get('The validation record was never written')}
                >
                    <Stack gap={'xs'}>
                        <Text size={'sm'}>
                            {productCopy('{product} has no record of publishing the validation record into the zone. Nothing is propagating and nothing will change by waiting — the write itself did not happen. This is a different problem from validation taking too long, and it has a different fix.')}
                        </Text>
                        <Text size={'sm'}>
                            {I18n.get('The usual cause is that the connection could not write to the zone at the moment the certificate was requested. Check that the connection is still active and can write records, then request the certificate again.')}
                        </Text>
                    </Stack>
                </Alert>
            )}

            {/* ⚠️ THE `STUCK` EXPLANATION. The record WAS written, so the
                question is whether it is still there and still correct — which
                is why this one links into the zone and the other does not. */}
            {!neverWritten && certificate.status === 'STUCK' && (
                <Alert
                    color={'orange'}
                    variant={'light'}
                    title={I18n.get('Validation is not progressing')}
                >
                    <Stack gap={'xs'}>
                        <Text size={'sm'}>
                            {I18n.get('The validation record was written, but the provider has not confirmed it within the expected time. Compare the record below against what is actually published in the zone — a value that does not match, or a record removed after it was written, is the usual cause.')}
                        </Text>
                        <Text size={'xs'} c={'dimmed'}>
                            {I18n.get('Written at')}: <Code>{certificate.validationRecordWrittenAt}</Code>
                            {' · '}
                            {I18n.get('Considered stuck after')} {certificate.stuckAfterMinutes} {I18n.get('minutes')}
                        </Text>
                        {/* Straight to the records in the zone that should hold
                            it, rather than telling an operator to go and find
                            the zone themselves. */}
                        {certificate.validationZoneId && (
                            <Anchor
                                component={NavLink}
                                size={'sm'}
                                to={config.zoneRecordsPath(
                                    certificate.validationZoneId,
                                    certificate.connectionId,
                                )}
                            >
                                {I18n.get('Open the records in that zone')}
                            </Anchor>
                        )}
                    </Stack>
                </Alert>
            )}

            {records.length > 0 && (
                <Stack gap={4}>
                    <Text size={'sm'} fw={500}>
                        {I18n.get('Validation records')}
                    </Text>
                    <Text size={'xs'} c={'dimmed'}>
                        {/* ⚠️ A CERTIFICATE COVERING SEVERAL NAMES HAS ONE
                            RECORD PER NAME, AND THEY SETTLE INDEPENDENTLY. A
                            reader seeing three rows where one says "SUCCESS"
                            and two do not should understand that as normal
                            partial progress rather than a contradiction. */}
                        {I18n.get('One record per name on the certificate. They are confirmed independently, so some may settle before others.')}
                    </Text>
                    <Table.ScrollContainer minWidth={600}>
                        <Table striped={true} withTableBorder={true} fz={'xs'}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{I18n.get('For name')}</Table.Th>
                                    <Table.Th>{I18n.get('Record')}</Table.Th>
                                    <Table.Th>{I18n.get('Value')}</Table.Th>
                                    <Table.Th>{I18n.get('Provider status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {records.map((record, index) => (
                                    <Table.Tr key={`${record.name}#${record.domainName}#${index}`}>
                                        <Table.Td>{record.domainName}</Table.Td>
                                        <Table.Td>
                                            <Group gap={4} wrap={'nowrap'}>
                                                <Badge variant={'light'} size={'sm'}>
                                                    {record.type}
                                                </Badge>
                                                <Code>{record.name}</Code>
                                            </Group>
                                        </Table.Td>
                                        <Table.Td>
                                            <Code style={{wordBreak: 'break-all'}}>
                                                {record.value}
                                            </Code>
                                        </Table.Td>
                                        <Table.Td>
                                            {/* The provider's own word for this
                                                name, VERBATIM and unmapped —
                                                it is not Flexi's vocabulary and
                                                coercing it into
                                                CertificateStatus would invent a
                                                claim the provider did not make. */}
                                            <Text size={'xs'} c={record.status ? undefined : 'dimmed'}>
                                                {record.status ?? I18n.get('not reported')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </Stack>
            )}

        </Stack>
    );
};
